import { ActionTree } from 'vuex';
import {
    Actions,
    AugmentedActionContext,
    MutationTypes,
    State,
    TodoListApiData, TodoListItemApiData, TodoListMemberApiData,
    TodoListsApiData
} from '../types/todo-lists.types';
import { TodoList } from '../../../scripts/api/models/todoList';
import { TodoListItem } from '../../../scripts/api/models/todoListItem';
import Api from '../../../scripts/api';

import { STATES } from './state';
import { TodoListMember } from '../../../scripts/api/models/todoListMember';

const actions: ActionTree<State, State> & Actions = {
    setCurrentTodoList ({ commit }: AugmentedActionContext, todoList: TodoList) {
        commit(MutationTypes.SET_CURRENT_TODO_LIST, todoList);
    },
    async fetchTodoLists ({ commit, state }: AugmentedActionContext): Promise<void> {
        if (state.todoListsState !== STATES.TODO_LISTS_INIT) {
            return;
        }

        commit(MutationTypes.TODO_LISTS_FETCHING);

        const result: TodoListsApiData | void = await Api.version(1).get<TodoListsApiData>('todolist');

        if (typeof result !== 'undefined') {
            commit(MutationTypes.TODO_LISTS_FETCHED, result);
        }
    },
    addTodoList ({ commit, state }: AugmentedActionContext, todoList: TodoList): void {
        if (state.todoListsState !== STATES.TODO_LISTS_FETCHED) {
            return;
        }

        commit(MutationTypes.TODO_LIST_ADDED, todoList);
    },
    async saveNewTodoList ({ commit, state }: AugmentedActionContext, todoList: TodoList): Promise<void> {
        if (state.todoListsState !== STATES.TODO_LISTS_FETCHED) {
            return;
        }

        commit(MutationTypes.TODO_LIST_NEW_LIST_SAVING);

        const result: TodoListApiData | void = await Api.version(1).put<TodoListApiData>(
            `todolist`,
            { title: todoList.title }
        );

        if (typeof result !== 'undefined') {
            commit(MutationTypes.TODO_LIST_ADDED, result);
            commit(MutationTypes.TODO_LIST_REMOVED, todoList);
        }
    },
    async removeTodoList ({ commit, state }: AugmentedActionContext, todoList: TodoList): Promise<void> {
        if (state.todoListsState !== STATES.TODO_LISTS_FETCHED) {
            return;
        }

        commit(MutationTypes.TODO_LIST_REMOVING);
        if (todoList.uid > 0) {
            await Api.version(1).delete<TodoListApiData>(`todolist/${todoList.uid}`);
        }

        commit(MutationTypes.TODO_LIST_REMOVED, todoList);
    },
    async updateTodoList ({ commit, state }: AugmentedActionContext, todoList: TodoList): Promise<void> {
        if (state.todoListsState !== STATES.TODO_LISTS_FETCHED) {
            return;
        }

        commit(MutationTypes.TODO_LIST_UPDATING);
        await Api.version(1).post<TodoListApiData>(`todolist/${todoList.uid}`, todoList);

        commit(MutationTypes.TODO_LIST_UPDATED, todoList);
    },
    addTodoListItem ({ commit, state }: AugmentedActionContext, todoListItem: TodoListItem): void {
        if (state.currentTodoList === null) {
            return;
        }

        commit(MutationTypes.TODO_LIST_ITEM_ADDED, todoListItem);
    },
    async saveNewTodoListItem ({ commit, state }: AugmentedActionContext, todoListItem: TodoListItem): Promise<void> {
        if (state.currentTodoList === null) {
            return;
        }

        commit(MutationTypes.TODO_LIST_NEW_LIST_ITEM_SAVING);

        const result: TodoListItemApiData | void = await Api.version(1).put<TodoListItemApiData>(`todolist/${state.currentTodoList.uid}/items`, {
            title: todoListItem.title,
            done: todoListItem.done,
            priority: todoListItem.priority,
        });

        if (typeof result !== 'undefined') {
            commit(MutationTypes.TODO_LIST_ITEM_ADDED, result);
            commit(MutationTypes.TODO_LIST_ITEM_REMOVED, todoListItem);
        }
    },
    async removeTodoListItem ({ commit, state }: AugmentedActionContext, todoListItem: TodoListItem): Promise<void> {
        if (state.todoListsState !== STATES.TODO_LISTS_FETCHED || state.currentTodoList === null) {
            return;
        }

        commit(MutationTypes.TODO_LIST_ITEM_REMOVING);

        if (todoListItem.uid > 0) {
            await Api.version(1).delete(`todolist/${state.currentTodoList.uid}/items/${todoListItem.uid}`);
        }

        commit(MutationTypes.TODO_LIST_ITEM_REMOVED, todoListItem);
    },
    async updateTodoListItem ({ commit, state }: AugmentedActionContext, todoListItem: TodoListItem): Promise<void> {
        if (state.todoListsState !== STATES.TODO_LISTS_FETCHED || state.currentTodoList === null) {
            return;
        }

        await Api.version(1).post<TodoListItemApiData>(`todolist/${state.currentTodoList.uid}/items/${todoListItem.uid}`, todoListItem);

        commit(MutationTypes.TODO_LIST_ITEM_UPDATED, todoListItem);
    },
    async toggleTodoListItem ({ commit, state }: AugmentedActionContext, todoListItem: TodoListItem): Promise<void> {
        if (state.todoListsState !== STATES.TODO_LISTS_FETCHED || state.currentTodoList === null) {
            return;
        }

        await Api.version(1).post<TodoListItemApiData>(`todolist/${state.currentTodoList.uid}/items/${todoListItem.uid}/toggle`, todoListItem);

        commit(MutationTypes.TODO_LIST_ITEM_UPDATED, todoListItem);
    },
    async addTodoListMember ({ commit }: AugmentedActionContext, payload: { todoList: TodoList, member: TodoListMember }): Promise<void> {
        const { todoList, member } = payload;
        commit(MutationTypes.TODO_LIST_MEMBER_ADDING);
        await Api.version(1).put<TodoListMemberApiData>(`todolist/${todoList.uid}/members/${member.uid}`, {});

        commit(MutationTypes.TODO_LIST_MEMBER_ADDED, payload);
    },
    async removeTodoListMember ({ commit }: AugmentedActionContext, payload: { todoList: TodoList, member: TodoListMember }): Promise<void> {
        const { todoList, member } = payload;
        commit(MutationTypes.TODO_LIST_MEMBER_REMOVING);
        await Api.version(1).delete<TodoListMemberApiData>(`todolist/${todoList.uid}/members/${member.uid}`, {});

        commit(MutationTypes.TODO_LIST_MEMBER_REMOVED, payload);
    },
    async removeCurrentUserFromList ({ commit }: AugmentedActionContext, todoList: TodoList): Promise<void> {
        commit(MutationTypes.TODO_LIST_MEMBER_REMOVING);
        await Api.version(1).delete<TodoListMember>(`todolist/${todoList.uid}/members`, {});

        commit(MutationTypes.TODO_LIST_MEMBER_REMOVED, { todoList, member: null });
    },
};

export default actions;
